// Wave 3.51 — RequestEditDialog
//
// Universal dialog used to create/submit Edit Transaction Requests
// across Phase A and Phase B entity types:
//
//   Phase A
//     - 'goldsmith_issue'    : top-level + per-line qty/grams + (issue
//                               only) order/requirement re-routing
//     - 'goldsmith_return'   : top-level + per-line qty/grams
//
//   Phase B
//     - 'vendor_purchase_return' : top-level + per-line qty + rate + note
//     - 'packet_transfer'        : top-level + qty (no target swap; B.2)
//     - 'packet_recut'           : top-level + qty (no target swap; B.2)
//     - 'packet_send_to_broken'  : top-level + qty (no target swap; B.2)
//     - 'jewellery_receive'      : top-level only (weight cascade -> B.2)
//
// Props:
//   - entityType    (required)  one of the 7 keys above
//   - entity        (optional)  pre-fetched entity doc (Phase A host pages
//                               pass this in directly). When omitted the
//                               dialog will fetch the snapshot via
//                               GET /api/edit-requests/entity/{type}/{id}.
//   - entityId      (optional)  used to fetch snapshot when entity is
//                               not supplied (Phase B host pages).
//   - triggerLabel  (optional)  button text; default "Request Edit"
//   - testId        (optional)  data-testid for the trigger button
//   - onSuccess     (optional)  callback(res) after a successful submit
//
// The dialog also handles:
//   - CA/SA-only initiator guard (renders nothing for plain users)
//   - T+1 IST window guard (renders nothing once window has passed)
//   - "Editable till <DD MMM YYYY>" badge (rather than a static T+0 badge)

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useAuth } from '@/lib/auth';
import { submitEdit, isWithinEditWindowT1, editWindowEndDateT1 } from '@/lib/editFlow';
import { extractError } from '@/lib/errors';
import { api } from '@/lib/api';
import { PencilLine } from 'lucide-react';

const CARAT_MATS = new Set(['diamond', 'color_stone', 'pearl']);
const PHASE_A_TYPES = new Set(['goldsmith_issue', 'goldsmith_return']);
const PACKET_FAMILY_TYPES = new Set(['packet_transfer', 'packet_recut', 'packet_send_to_broken']);
const TOP_ONLY_TYPES = new Set(['jewellery_receive']);

function lineIsLockedForQty(ln) {
  return (
    (Array.isArray(ln.attributions) && ln.attributions.length > 0) ||
    (Array.isArray(ln.returns) && ln.returns.length > 0)
  );
}

/**
 * @param {object} props
 * @param {string} props.entityType  See module header for accepted values.
 * @param {object} [props.entity]    Pre-fetched entity doc (Phase A).
 * @param {string} [props.entityId]  Entity id; used to fetch via API (Phase B).
 * @param {string} [props.triggerLabel]
 * @param {string} [props.testId]
 * @param {function} [props.onSuccess]
 */
export function RequestEditDialog({
  entityType,
  entity: entityProp,
  entityId: entityIdProp,
  triggerLabel = 'Request Edit',
  testId = 'request-edit-trigger',
  onSuccess,
  // Controlled mode (used by kebab menus / external triggers). When
  // ``renderTrigger`` is false, the dialog renders no Button and relies
  // on the parent to toggle ``open`` via ``onOpenChange``.
  renderTrigger = true,
  open: openProp,
  onOpenChange,
}) {
  const { isSuperAdmin, isCompanyAdmin } = useAuth();
  const navigate = useNavigate();

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    if (onOpenChange) onOpenChange(next);
  };
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchedEntity, setFetchedEntity] = useState(null);

  // Effective entity: prop > fetched snapshot.
  const entity = entityProp || fetchedEntity;
  const entityId = entityProp?.id || entityIdProp || fetchedEntity?.id;

  // Fetch snapshot if the host page didn't pre-supply the entity.
  useEffect(() => {
    if (!open) return undefined;
    if (entityProp || fetchedEntity) return undefined;
    if (!entityIdProp) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/edit-requests/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityIdProp)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setFetchedEntity(data?.snapshot || null);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(extractError(e, 'Failed to load entity for edit'));
        setOpen(false);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, entityType, entityIdProp, entityProp, fetchedEntity]);

  // -----------------------------------------------------------------------
  // Form state — initialized lazily once entity is available.
  // -----------------------------------------------------------------------
  const [reason, setReason] = useState('');
  const [topRef, setTopRef] = useState('');
  const [topNote, setTopNote] = useState('');
  // Phase A line drafts (issues + returns)
  const [lineDrafts, setLineDrafts] = useState([]);
  // Phase B (purchase return) line drafts
  const [prLineDrafts, setPrLineDrafts] = useState([]);
  // Phase B (packet family) single-row draft
  const [pktDraft, setPktDraft] = useState({ carats: 0, pieces: 0 });
  // Phase B.2.B (jewellery cascade) full draft
  const [jewDraft, setJewDraft] = useState({
    gross_weight_grams: 0,
    diamond_carats: 0, diamond_pieces: 0,
    color_stone_carats: 0, color_stone_pieces: 0,
    pearl_carats: 0, pearl_pieces: 0,
    labour_rate_per_gram: 0,
    gold_loss_percent: 0, gold_loss_mode: 'gold_grams',
    gold_rate_999_at_receipt: 0,
    credit_days: null,
    consumption: [],
  });

  // Open orders cache for Phase A.3 re-routing (issue only)
  const goldsmithId = entity?.goldsmith_id;
  const [openOrders, setOpenOrders] = useState([]);
  useEffect(() => {
    if (!open || !goldsmithId || entityType !== 'goldsmith_issue') return undefined;
    let cancelled = false;
    api
      .get('/orders/', { params: { goldsmith_id: goldsmithId, status: 'open' } })
      .then(({ data }) => {
        if (cancelled) return;
        const items = Array.isArray(data) ? data : (data?.items || []);
        setOpenOrders(items.filter((o) => o.stage !== 'Completed'));
      })
      .catch(() => setOpenOrders([]));
    return () => { cancelled = true; };
  }, [open, goldsmithId, entityType]);

  // (Re-)initialize draft state every time entity changes (e.g. after fetch).
  useEffect(() => {
    if (!entity) return;
    setTopRef(entity.reference || '');
    setTopNote(entity.note || '');

    if (PHASE_A_TYPES.has(entityType)) {
      setLineDrafts(
        (entity.lines || []).map((ln) => {
          const key = entityType === 'goldsmith_return' ? ln.ledger_entry_id : ln.line_id;
          const mat = ln.material_type || ln.kind;
          return {
            key,
            ln,
            material_type: mat,
            carats: ln.carats ?? 0,
            pieces: ln.pieces ?? 0,
            grams: ln.grams ?? 0,
            note: ln.note ?? '',
            order_id: ln.order_id ?? null,
            requirement_id: ln.requirement_id ?? null,
            lockedForQty: lineIsLockedForQty(ln),
          };
        })
      );
    } else if (entityType === 'vendor_purchase_return') {
      setPrLineDrafts(
        (entity.lines || []).map((ln) => ({
          key: ln.ledger_id,
          ln,
          carats: ln.carats ?? 0,
          pieces: ln.pieces ?? 0,
          rate_per_ct: ln.rate_per_ct ?? 0,
          note: ln.note ?? '',
        }))
      );
    } else if (PACKET_FAMILY_TYPES.has(entityType)) {
      setPktDraft({
        carats: entity.carats ?? 0,
        pieces: entity.pieces ?? 0,
        // Phase B.2.A — target packet swap (packet_transfer only).
        target_packet_id: entity.target_packet_id ?? null,
      });
    } else if (entityType === 'jewellery_receive') {
      // Phase B.2.B — weight cascade fields.
      const consumed = (entity.rm_consumed_snapshot || []).map((cs, i) => ({
        key: `${cs.source_issue_id || ''}|${cs.source_line_id || ''}|${cs.material_type || ''}|${i}`,
        issue_id: cs.source_issue_id || '',
        line_id: cs.source_line_id || '',
        material_type: cs.material_type || '',
        packet_no: cs.packet_no || cs.purity_name || '',
        consumed_carats: cs.carats ?? 0,
        consumed_pieces: cs.pieces ?? 0,
        consumed_grams: cs.grams ?? 0,
        overflow_destination: 'unattributed_goldsmith',
      }));
      // Map overflow destinations onto the consumed rows.
      for (const ov of (entity.rm_overflow_snapshot || [])) {
        const m = consumed.find(
          (c) =>
            c.issue_id === ov.source_issue_id &&
            c.line_id === ov.source_line_id &&
            c.material_type === ov.material_type,
        );
        if (m && ov.destination) m.overflow_destination = ov.destination;
      }
      setJewDraft({
        gross_weight_grams: entity.gross_weight_grams ?? 0,
        diamond_carats: entity.diamond_carats ?? 0,
        diamond_pieces: entity.diamond_pieces ?? 0,
        color_stone_carats: entity.color_stone_carats ?? 0,
        color_stone_pieces: entity.color_stone_pieces ?? 0,
        pearl_carats: entity.pearl_carats ?? 0,
        pearl_pieces: entity.pearl_pieces ?? 0,
        labour_rate_per_gram: entity.labour_rate_per_gram ?? 0,
        gold_loss_percent: entity.gold_loss_percent ?? 0,
        gold_loss_mode: entity.gold_loss_mode || 'gold_grams',
        gold_rate_999_at_receipt: entity.gold_rate_999_at_receipt ?? 0,
        credit_days: entity.credit_days ?? null,
        consumption: consumed,
      });
    }
  }, [entity, entityType]);

  // Phase B.2.A — Eligible swap targets cache for packet_transfer.
  const [swapTargets, setSwapTargets] = useState([]);
  const [swapTargetsLoading, setSwapTargetsLoading] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    if (entityType !== 'packet_transfer') return undefined;
    if (!entityId) return undefined;
    let cancelled = false;
    setSwapTargetsLoading(true);
    api
      .get(`/edit-requests/swap-targets/packet_transfer/${encodeURIComponent(entityId)}`)
      .then(({ data }) => { if (!cancelled) setSwapTargets(data?.items || []); })
      .catch(() => { if (!cancelled) setSwapTargets([]); })
      .finally(() => { if (!cancelled) setSwapTargetsLoading(false); });
    return () => { cancelled = true; };
  }, [open, entityType, entityId]);

  // T+1 window check
  const inWindow = useMemo(
    () => isWithinEditWindowT1(entity?.created_at),
    [entity?.created_at]
  );
  const editEndDate = useMemo(
    () => editWindowEndDateT1(entity?.created_at),
    [entity?.created_at]
  );

  const canInitiate = isSuperAdmin || isCompanyAdmin;

  // -----------------------------------------------------------------------
  // Gating: hide the trigger if the user can't initiate. Once a snapshot
  // is loaded we additionally check the T+1 window. We can't decide
  // window status without `created_at`, so we render the trigger
  // optimistically and re-validate on open if entity is fetched
  // lazily.
  // -----------------------------------------------------------------------
  if (!canInitiate) return null;
  if (entityProp && !inWindow) return null;

  const reset = () => {
    setReason('');
    if (entity) {
      setTopRef(entity.reference || '');
      setTopNote(entity.note || '');
    }
    setSubmitting(false);
  };

  // -----------------------------------------------------------------------
  // Patch builders (per entity type)
  // -----------------------------------------------------------------------
  const buildPhaseAPatch = () => {
    const patch = {};
    if ((topRef || '') !== (entity?.reference || '')) patch.reference = topRef;
    if ((topNote || '') !== (entity?.note || '')) patch.note = topNote;
    const linePatches = [];
    for (const d of lineDrafts) {
      const lp = {};
      if (entityType === 'goldsmith_return') lp.ledger_entry_id = d.key;
      else lp.line_id = d.key;
      if ((d.note || '') !== (d.ln.note || '')) lp.note = d.note;
      if (!d.lockedForQty) {
        if (CARAT_MATS.has(d.material_type)) {
          if (Number(d.carats) !== Number(d.ln.carats || 0)) lp.carats = Number(d.carats) || 0;
          if (Number(d.pieces) !== Number(d.ln.pieces || 0)) lp.pieces = Number(d.pieces) || 0;
        } else if (d.material_type === 'gold') {
          if (Number(d.grams) !== Number(d.ln.grams || 0)) lp.grams = Number(d.grams) || 0;
        }
        if (entityType === 'goldsmith_issue') {
          const oldOrder = d.ln.order_id ?? null;
          const oldReq = d.ln.requirement_id ?? null;
          const newOrder = d.order_id ?? null;
          const newReq = d.requirement_id ?? null;
          if (newOrder !== oldOrder) lp.order_id = newOrder;
          if (newReq !== oldReq) lp.requirement_id = newReq;
        }
      }
      const keys = Object.keys(lp).filter((k) => k !== 'line_id' && k !== 'ledger_entry_id');
      if (keys.length > 0) linePatches.push(lp);
    }
    if (linePatches.length > 0) patch.lines = linePatches;
    return patch;
  };

  const buildPRPatch = () => {
    const patch = {};
    if ((topRef || '') !== (entity?.reference || '')) patch.reference = topRef;
    if ((topNote || '') !== (entity?.note || '')) patch.note = topNote;
    const linePatches = [];
    for (const d of prLineDrafts) {
      const lp = { ledger_id: d.key };
      if (Number(d.carats) !== Number(d.ln.carats || 0)) lp.carats = Number(d.carats) || 0;
      if (Number(d.pieces) !== Number(d.ln.pieces || 0)) lp.pieces = Number(d.pieces) || 0;
      if (Number(d.rate_per_ct) !== Number(d.ln.rate_per_ct || 0)) lp.rate_per_ct = Number(d.rate_per_ct) || 0;
      if ((d.note || '') !== (d.ln.note || '')) lp.note = d.note;
      const keys = Object.keys(lp).filter((k) => k !== 'ledger_id');
      if (keys.length > 0) linePatches.push(lp);
    }
    if (linePatches.length > 0) patch.lines = linePatches;
    return patch;
  };

  const buildPacketFamilyPatch = () => {
    const patch = {};
    if ((topRef || '') !== (entity?.reference || '')) patch.reference = topRef;
    if ((topNote || '') !== (entity?.note || '')) patch.note = topNote;
    if (Number(pktDraft.carats) !== Number(entity?.carats || 0)) patch.carats = Number(pktDraft.carats) || 0;
    if (Number(pktDraft.pieces) !== Number(entity?.pieces || 0)) patch.pieces = Number(pktDraft.pieces) || 0;
    // Phase B.2.A — target packet swap (packet_transfer only).
    if (entityType === 'packet_transfer') {
      const oldId = entity?.target_packet_id || null;
      const newId = pktDraft.target_packet_id || null;
      if (newId && newId !== oldId) patch.target_packet_id = newId;
    }
    return patch;
  };

  const buildTopOnlyPatch = () => {
    const patch = {};
    if ((topRef || '') !== (entity?.reference || '')) patch.reference = topRef;
    if ((topNote || '') !== (entity?.note || '')) patch.note = topNote;
    return patch;
  };

  const buildJewelleryCascadePatch = () => {
    // Start with top-level (always allowed).
    const patch = buildTopOnlyPatch();
    if (!entity) return patch;
    const numFields = [
      'gross_weight_grams',
      'diamond_carats', 'diamond_pieces',
      'color_stone_carats', 'color_stone_pieces',
      'pearl_carats', 'pearl_pieces',
      'labour_rate_per_gram',
      'gold_loss_percent',
      'gold_rate_999_at_receipt',
    ];
    for (const k of numFields) {
      const cur = Number(jewDraft[k]) || 0;
      const old = Number(entity[k]) || 0;
      if (cur !== old) patch[k] = cur;
    }
    if ((jewDraft.gold_loss_mode || '') !== (entity.gold_loss_mode || '')) {
      patch.gold_loss_mode = jewDraft.gold_loss_mode || null;
    }
    if ((jewDraft.credit_days ?? null) !== (entity.credit_days ?? null)) {
      patch.credit_days = jewDraft.credit_days ?? null;
    }
    // Consumption: send the WHOLE list only if any line changed
    // (downstream replay treats consumption as a wholesale list).
    const origLines = (entity.rm_consumed_snapshot || []).map((cs) => ({
      issue_id: cs.source_issue_id,
      line_id: cs.source_line_id,
      material_type: cs.material_type,
      consumed_carats: Number(cs.carats || 0),
      consumed_pieces: Number(cs.pieces || 0),
      consumed_grams: Number(cs.grams || 0),
    }));
    let consumptionChanged = false;
    if (origLines.length !== jewDraft.consumption.length) consumptionChanged = true;
    else {
      for (let i = 0; i < origLines.length; i += 1) {
        const o = origLines[i];
        const c = jewDraft.consumption[i];
        if (!c) { consumptionChanged = true; break; }
        if (
          o.issue_id !== c.issue_id || o.line_id !== c.line_id || o.material_type !== c.material_type
          || Number(c.consumed_carats || 0) !== o.consumed_carats
          || Number(c.consumed_pieces || 0) !== o.consumed_pieces
          || Number(c.consumed_grams || 0) !== o.consumed_grams
        ) { consumptionChanged = true; break; }
      }
    }
    if (consumptionChanged) {
      patch.consumption = jewDraft.consumption.map((c) => ({
        issue_id: c.issue_id,
        line_id: c.line_id,
        material_type: c.material_type,
        consumed_carats: Number(c.consumed_carats) || 0,
        consumed_pieces: Number(c.consumed_pieces) || 0,
        consumed_grams: Number(c.consumed_grams) || 0,
        overflow_destination: c.overflow_destination || 'unattributed_goldsmith',
      }));
    }
    return patch;
  };

  const buildPatch = () => {
    if (PHASE_A_TYPES.has(entityType)) return buildPhaseAPatch();
    if (entityType === 'vendor_purchase_return') return buildPRPatch();
    if (PACKET_FAMILY_TYPES.has(entityType)) return buildPacketFamilyPatch();
    if (entityType === 'jewellery_receive') return buildJewelleryCascadePatch();
    if (TOP_ONLY_TYPES.has(entityType)) return buildTopOnlyPatch();
    return {};
  };

  // -----------------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------------
  const computeEntityLabel = () => {
    if (!entity) return entityId || '';
    if (entityType === 'goldsmith_issue') return entity.issue_memo_no || entity.issue_no || entityId;
    if (entityType === 'goldsmith_return') return entity.return_memo_no || entity.return_no || entityId;
    if (entityType === 'vendor_purchase_return') return entity.memo_no || entityId;
    if (PACKET_FAMILY_TYPES.has(entityType)) {
      return `${entity.source_packet_no || ''} → ${entity.target_packet_no || ''}`;
    }
    if (entityType === 'jewellery_receive') return entity.jewellery_no || entity.id || entityId;
    return entityId || '';
  };

  const handleSubmit = async () => {
    if (!entity) {
      toast.error('Entity snapshot not loaded yet');
      return;
    }
    if (!inWindow) {
      toast.error('This transaction is outside the T+1 edit window');
      return;
    }
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      toast.error('No changes to submit');
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitEdit({
        entity_type: entityType,
        entity_id: entityId,
        entity_label: computeEntityLabel(),
        reason,
        patch,
        company_id: entity.company_id,
      });
      if (res?.edited) {
        toast.success('Edit applied (Super Admin)');
      } else if (res?.pending) {
        toast.success('Edit request submitted — awaiting Super Admin approval');
      } else {
        toast.success('Edit request submitted');
      }
      setOpen(false);
      reset();
      if (onSuccess) onSuccess(res);
    } catch (e) {
      toast.error(extractError(e, 'Submit failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  const renderBadge = () => (
    <Badge className="bg-amber-50 text-amber-700 text-[10px]" data-testid="request-edit-window-badge">
      {editEndDate ? `Editable till ${editEndDate}` : 'T+1 edit window'}
    </Badge>
  );

  const renderTopFields = () => (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-[11px] text-slate-500">Reference</Label>
        <Input
          value={topRef}
          onChange={(e) => setTopRef(e.target.value)}
          placeholder="Optional reference"
          data-testid="request-edit-top-reference"
        />
      </div>
      <div>
        <Label className="text-[11px] text-slate-500">Note</Label>
        <Input
          value={topNote}
          onChange={(e) => setTopNote(e.target.value)}
          placeholder="Optional note"
          data-testid="request-edit-top-note"
        />
      </div>
    </div>
  );

  const renderPhaseALines = () => (
    <div className="border-t border-[var(--divider)] pt-3">
      <div className="text-sm font-medium text-[var(--ink-navy)] mb-2">Line Items</div>
      <div className="space-y-3" data-testid="request-edit-lines">
        {lineDrafts.map((d, idx) => {
          const mt = d.material_type;
          const isCarat = CARAT_MATS.has(mt);
          const isGold = mt === 'gold';
          const label = d.ln.packet_no || d.ln.purity_name || `Line ${idx + 1}`;
          return (
            <div
              key={d.key || idx}
              className="border border-[var(--divider)] rounded p-3 bg-slate-50/40"
              data-testid={`request-edit-line-${idx}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary" className="text-[10px] capitalize">
                  {(mt || '').replace('_', ' ')}
                </Badge>
                <span className="font-mono text-[12px] text-slate-700">{label}</span>
                {d.lockedForQty && (
                  <Badge className="bg-slate-200 text-slate-700 text-[10px]" data-testid={`request-edit-line-${idx}-locked-qty`}>
                    Qty locked (has downstream activity)
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                {isCarat && (
                  <>
                    <div>
                      <Label className="text-[11px] text-slate-500">Carats</Label>
                      <Input
                        type="number"
                        step="0.001"
                        value={d.carats}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, carats: v } : p)));
                        }}
                        disabled={d.lockedForQty}
                        data-testid={`request-edit-line-${idx}-carats`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500">Pieces</Label>
                      <Input
                        type="number"
                        step="1"
                        value={d.pieces}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, pieces: v } : p)));
                        }}
                        disabled={d.lockedForQty}
                        data-testid={`request-edit-line-${idx}-pieces`}
                      />
                    </div>
                  </>
                )}
                {isGold && (
                  <div>
                    <Label className="text-[11px] text-slate-500">Grams</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={d.grams}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, grams: v } : p)));
                      }}
                      disabled={d.lockedForQty}
                      data-testid={`request-edit-line-${idx}-grams`}
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <Label className="text-[11px] text-slate-500">Line note</Label>
                  <Input
                    value={d.note}
                    onChange={(e) => {
                      const v = e.target.value;
                      setLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, note: v } : p)));
                    }}
                    placeholder="Optional"
                    data-testid={`request-edit-line-${idx}-note`}
                  />
                </div>
              </div>
              {entityType === 'goldsmith_issue' && !d.lockedForQty && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 pt-2 border-t border-dashed border-amber-200">
                  <div>
                    <Label className="text-[11px] text-slate-500">Order Form (re-route)</Label>
                    {(() => {
                      const orderItems = [
                        { id: '__unattributed__', order_no: 'Unattributed', stage: '' },
                        ...openOrders,
                      ];
                      return (
                        <SearchableSelect
                          items={orderItems}
                          value={d.order_id ? d.order_id : '__unattributed__'}
                          onValueChange={(v) => {
                            const newOrderId = !v || v === '__unattributed__' ? null : v;
                            setLineDrafts((prev) =>
                              prev.map((p) =>
                                p.key === d.key
                                  ? { ...p, order_id: newOrderId, requirement_id: null }
                                  : p
                              )
                            );
                          }}
                          itemToLabel={(o) => (o.id === '__unattributed__' ? 'Unattributed' : `${o.order_no || 'OF-?'} · ${o.stage || ''}`)}
                          searchKeys={['order_no', 'stage', 'title']}
                          placeholder="Select order…"
                          searchPlaceholder="Search order…"
                          testId={`request-edit-line-${idx}-order`}
                        />
                      );
                    })()}
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Requirement (optional)</Label>
                    {(() => {
                      const reqList = (openOrders.find((o) => o.id === d.order_id)?.requirements || [])
                        .filter((r) => {
                          if (CARAT_MATS.has(d.material_type)) {
                            return r.material_type === d.material_type;
                          }
                          return r.material_type === 'gold';
                        })
                        .map((r) => ({
                          id: r.id,
                          label: `${r.classification_schema || r.purity_name || r.material_type} · ${CARAT_MATS.has(d.material_type) ? `${r.carats || 0} ct / ${r.pieces || 0} pcs` : `${r.grams || 0} g`}`,
                        }));
                      const items = [{ id: '__none__', label: 'None' }, ...reqList];
                      return (
                        <SearchableSelect
                          items={items}
                          value={d.requirement_id ? d.requirement_id : '__none__'}
                          onValueChange={(v) => {
                            const newReqId = !v || v === '__none__' ? null : v;
                            setLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, requirement_id: newReqId } : p)));
                          }}
                          itemToValue={(it) => it.id}
                          itemToLabel={(it) => it.label}
                          searchKeys={['label']}
                          placeholder={d.order_id ? 'Select requirement…' : 'Pick an order first'}
                          searchPlaceholder="Search requirement…"
                          disabled={!d.order_id}
                          testId={`request-edit-line-${idx}-requirement`}
                        />
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderPRLines = () => (
    <div className="border-t border-[var(--divider)] pt-3">
      <div className="text-sm font-medium text-[var(--ink-navy)] mb-2">Purchase Return Lines</div>
      <div className="space-y-3" data-testid="request-edit-pr-lines">
        {prLineDrafts.map((d, idx) => (
          <div
            key={d.key || idx}
            className="border border-[var(--divider)] rounded p-3 bg-slate-50/40"
            data-testid={`request-edit-pr-line-${idx}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary" className="text-[10px] capitalize">
                {(entity?.material_kind || '').replace('_', ' ')}
              </Badge>
              <span className="font-mono text-[12px] text-slate-700">{d.ln.packet_no || `Line ${idx + 1}`}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">Carats</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={d.carats}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPrLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, carats: v } : p)));
                  }}
                  data-testid={`request-edit-pr-line-${idx}-carats`}
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Pieces</Label>
                <Input
                  type="number"
                  step="1"
                  value={d.pieces}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPrLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, pieces: v } : p)));
                  }}
                  data-testid={`request-edit-pr-line-${idx}-pieces`}
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Rate (₹/ct)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={d.rate_per_ct}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPrLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, rate_per_ct: v } : p)));
                  }}
                  data-testid={`request-edit-pr-line-${idx}-rate`}
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Line note</Label>
                <Input
                  value={d.note}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPrLineDrafts((prev) => prev.map((p) => (p.key === d.key ? { ...p, note: v } : p)));
                  }}
                  placeholder="Optional"
                  data-testid={`request-edit-pr-line-${idx}-note`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPacketFamilyFields = () => {
    const isTransfer = entityType === 'packet_transfer';
    return (
      <div className="border-t border-[var(--divider)] pt-3">
        <div className="text-sm font-medium text-[var(--ink-navy)] mb-2">Transfer Quantities</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="request-edit-packet-family">
          <div>
            <Label className="text-[11px] text-slate-500">Source Packet</Label>
            <div className="text-sm font-mono mt-1.5">{entity?.source_packet_no || '—'}</div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] text-slate-500">Target Packet</Label>
            {isTransfer ? (() => {
              const items = [
                ...(entity?.target_packet_id ? [{ id: entity.target_packet_id, label: `${entity.target_packet_no || entity.target_packet_id} (current)`, sub: 'Current' }] : []),
                ...swapTargets
                  .filter((p) => p.id !== entity?.target_packet_id)
                  .map((p) => ({
                    id: p.id,
                    label: `${p.packet_no || p.id} · ${(p.balance_carats ?? 0).toFixed(3)} ct / ${p.balance_pieces ?? 0} pcs`,
                    sub: p.classification_shape || p.classification_schema || null,
                  })),
              ];
              return (
                <div className="mt-1">
                  <SearchableSelect
                    items={items}
                    value={pktDraft.target_packet_id || (entity?.target_packet_id || '__none__')}
                    onValueChange={(v) => setPktDraft((p) => ({ ...p, target_packet_id: !v || v === '__none__' ? null : v }))}
                    itemToValue={(it) => it.id}
                    itemToLabel={(it) => it.label}
                    itemToSubLabel={(it) => it.sub || null}
                    searchKeys={['label']}
                    placeholder={swapTargetsLoading ? 'Loading eligible packets…' : 'Pick target packet…'}
                    searchPlaceholder="Search packet…"
                    disabled={swapTargetsLoading}
                    testId="request-edit-pkt-target-select"
                  />
                </div>
              );
            })() : (
              <div className="text-sm font-mono mt-1.5">{entity?.target_packet_no || '—'}</div>
            )}
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Carats</Label>
            <Input
              type="number"
              step="0.001"
              value={pktDraft.carats}
              onChange={(e) => setPktDraft((p) => ({ ...p, carats: e.target.value }))}
              data-testid="request-edit-pkt-carats"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Pieces</Label>
            <Input
              type="number"
              step="1"
              value={pktDraft.pieces}
              onChange={(e) => setPktDraft((p) => ({ ...p, pieces: e.target.value }))}
              data-testid="request-edit-pkt-pieces"
            />
          </div>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          {isTransfer
            ? 'Target packet may be swapped to another active packet under the same classification & goldsmith.'
            : 'Target packet is system-managed for this transfer kind and cannot be changed.'}
        </div>
      </div>
    );
  };

  const renderJewelleryFields = () => (
    <div className="border-t border-[var(--divider)] pt-3 space-y-3" data-testid="request-edit-jewellery-cascade">
      <div className="text-sm font-medium text-[var(--ink-navy)]">Weight Cascade</div>
      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
        Heads up: this edit will <strong>reverse and replay</strong> the entire receipt.
        Source packet balances, overflow routing, labour credit, and order completion will all
        recompute automatically. Cancel anytime if the values look wrong.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <Label className="text-[11px] text-slate-500">Gross weight (g)</Label>
          <Input
            type="number" step="0.001"
            value={jewDraft.gross_weight_grams}
            onChange={(e) => setJewDraft((p) => ({ ...p, gross_weight_grams: e.target.value }))}
            data-testid="request-edit-jew-gross-weight"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Labour ₹/g</Label>
          <Input
            type="number" step="0.01"
            value={jewDraft.labour_rate_per_gram}
            onChange={(e) => setJewDraft((p) => ({ ...p, labour_rate_per_gram: e.target.value }))}
            data-testid="request-edit-jew-labour-rate"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Gold loss %</Label>
          <Input
            type="number" step="0.01"
            value={jewDraft.gold_loss_percent}
            onChange={(e) => setJewDraft((p) => ({ ...p, gold_loss_percent: e.target.value }))}
            data-testid="request-edit-jew-loss-percent"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Loss settle mode</Label>
          <Select
            value={jewDraft.gold_loss_mode || 'gold_grams'}
            onValueChange={(v) => setJewDraft((p) => ({ ...p, gold_loss_mode: v }))}
          >
            <SelectTrigger className="h-9" data-testid="request-edit-jew-loss-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gold_grams">Gold (grams)</SelectItem>
              <SelectItem value="rupees">Rupees</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Gold rate ₹/g (999)</Label>
          <Input
            type="number" step="0.01"
            value={jewDraft.gold_rate_999_at_receipt}
            onChange={(e) => setJewDraft((p) => ({ ...p, gold_rate_999_at_receipt: e.target.value }))}
            data-testid="request-edit-jew-gold-rate-999"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Credit days</Label>
          <Input
            type="number" step="1"
            value={jewDraft.credit_days ?? ''}
            onChange={(e) => setJewDraft((p) => ({ ...p, credit_days: e.target.value === '' ? null : Number(e.target.value) }))}
            placeholder="—"
            data-testid="request-edit-jew-credit-days"
          />
        </div>
      </div>

      <div>
        <div className="text-[12px] font-medium text-slate-700 mt-2 mb-1">Stone Totals</div>
        <div className="grid grid-cols-3 gap-2">
          <div className="flex gap-1">
            <Input type="number" step="0.001" value={jewDraft.diamond_carats}
              onChange={(e) => setJewDraft((p) => ({ ...p, diamond_carats: e.target.value }))}
              placeholder="Diamond ct" data-testid="request-edit-jew-d-ct" />
            <Input type="number" step="1" value={jewDraft.diamond_pieces}
              onChange={(e) => setJewDraft((p) => ({ ...p, diamond_pieces: e.target.value }))}
              placeholder="pcs" data-testid="request-edit-jew-d-pcs" />
          </div>
          <div className="flex gap-1">
            <Input type="number" step="0.001" value={jewDraft.color_stone_carats}
              onChange={(e) => setJewDraft((p) => ({ ...p, color_stone_carats: e.target.value }))}
              placeholder="CS ct" data-testid="request-edit-jew-cs-ct" />
            <Input type="number" step="1" value={jewDraft.color_stone_pieces}
              onChange={(e) => setJewDraft((p) => ({ ...p, color_stone_pieces: e.target.value }))}
              placeholder="pcs" data-testid="request-edit-jew-cs-pcs" />
          </div>
          <div className="flex gap-1">
            <Input type="number" step="0.001" value={jewDraft.pearl_carats}
              onChange={(e) => setJewDraft((p) => ({ ...p, pearl_carats: e.target.value }))}
              placeholder="Pearl ct" data-testid="request-edit-jew-p-ct" />
            <Input type="number" step="1" value={jewDraft.pearl_pieces}
              onChange={(e) => setJewDraft((p) => ({ ...p, pearl_pieces: e.target.value }))}
              placeholder="pcs" data-testid="request-edit-jew-p-pcs" />
          </div>
        </div>
      </div>

      {jewDraft.consumption.length > 0 && (
        <div>
          <div className="text-[12px] font-medium text-slate-700 mt-2 mb-1">Per-line consumption</div>
          <div className="space-y-2">
            {jewDraft.consumption.map((c, idx) => {
              const isGold = c.material_type === 'gold';
              return (
                <div key={c.key || idx} className="border border-[var(--divider)] rounded p-2 bg-slate-50/40">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="text-[10px] capitalize">
                      {(c.material_type || '').replace('_', ' ')}
                    </Badge>
                    <span className="font-mono text-[11px] text-slate-700">{c.packet_no || `Line ${idx + 1}`}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {isGold ? (
                      <div>
                        <Label className="text-[11px] text-slate-500">Consumed g</Label>
                        <Input type="number" step="0.001"
                          value={c.consumed_grams}
                          onChange={(e) => {
                            const v = e.target.value;
                            setJewDraft((p) => ({
                              ...p,
                              consumption: p.consumption.map((cc) => cc.key === c.key ? { ...cc, consumed_grams: v } : cc),
                            }));
                          }}
                          data-testid={`request-edit-jew-line-${idx}-grams`} />
                      </div>
                    ) : (
                      <>
                        <div>
                          <Label className="text-[11px] text-slate-500">Consumed ct</Label>
                          <Input type="number" step="0.001"
                            value={c.consumed_carats}
                            onChange={(e) => {
                              const v = e.target.value;
                              setJewDraft((p) => ({
                                ...p,
                                consumption: p.consumption.map((cc) => cc.key === c.key ? { ...cc, consumed_carats: v } : cc),
                              }));
                            }}
                            data-testid={`request-edit-jew-line-${idx}-carats`} />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Consumed pcs</Label>
                          <Input type="number" step="1"
                            value={c.consumed_pieces}
                            onChange={(e) => {
                              const v = e.target.value;
                              setJewDraft((p) => ({
                                ...p,
                                consumption: p.consumption.map((cc) => cc.key === c.key ? { ...cc, consumed_pieces: v } : cc),
                              }));
                            }}
                            data-testid={`request-edit-jew-line-${idx}-pieces`} />
                        </div>
                      </>
                    )}
                    <div>
                      <Label className="text-[11px] text-slate-500">Overflow → </Label>
                      <Select
                        value={c.overflow_destination || 'unattributed_goldsmith'}
                        onValueChange={(v) => setJewDraft((p) => ({
                          ...p,
                          consumption: p.consumption.map((cc) => cc.key === c.key ? { ...cc, overflow_destination: v } : cc),
                        }))}
                      >
                        <SelectTrigger className="h-9" data-testid={`request-edit-jew-line-${idx}-overflow`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unattributed_goldsmith">Unattributed pool</SelectItem>
                          <SelectItem value="return_to_packet">Return to packet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const renderEntityBody = () => {
    if (loading) return <div className="text-slate-500 text-sm py-6">Loading entity…</div>;
    if (!entity) {
      return <div className="text-rose-600 text-sm py-6">Entity not available.</div>;
    }
    if (PHASE_A_TYPES.has(entityType)) return renderPhaseALines();
    if (entityType === 'vendor_purchase_return') return renderPRLines();
    if (PACKET_FAMILY_TYPES.has(entityType)) return renderPacketFamilyFields();
    if (entityType === 'jewellery_receive') return renderJewelleryFields();
    if (TOP_ONLY_TYPES.has(entityType)) return null;
    return null;
  };

  const lockedFieldsBlurb = () => {
    if (entityType === 'vendor_purchase_return') return 'Locked fields: Vendor, Material, Packet.';
    if (entityType === 'packet_transfer') return 'Locked fields: Source Packet only. Target may be swapped within the same classification & goldsmith.';
    if (PACKET_FAMILY_TYPES.has(entityType)) return 'Locked fields: Source Packet, Target Packet (system-managed for recut / send-to-broken).';
    if (entityType === 'jewellery_receive') return 'Edits trigger a full reverse-and-replay of the receipt; downstream activity on the unattributed pool may block the edit.';
    return 'Locked fields: Goldsmith, Material/Packet (Phase A scope).';
  };

  return (
    <>
      {renderTrigger && (
        <Button
          variant="outline"
          className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={() => setOpen(true)}
          data-testid={testId}
        >
          <PencilLine size={14} className="mr-1.5" /> {triggerLabel}
        </Button>
      )}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilLine size={16} className="text-amber-600" />
              Request Edit
              {renderBadge()}
            </DialogTitle>
            <DialogDescription>
              {isSuperAdmin
                ? 'Super Admin: changes will be applied immediately on confirm.'
                : 'Company Admin: changes will be submitted for Super Admin approval.'}
              {' '}{lockedFieldsBlurb()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {renderTopFields()}
            {renderEntityBody()}

            <div>
              <Label className="text-[11px] text-slate-500">
                Reason {isCompanyAdmin && <span className="text-slate-400">(visible to Super Admin)</span>}
              </Label>
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this edit needed?"
                data-testid="request-edit-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => navigate('/edit-requests')}
              data-testid="request-edit-open-page"
            >
              View Edit Requests
            </Button>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleSubmit}
              disabled={submitting || loading || !entity || !inWindow}
              data-testid="request-edit-submit"
            >
              {submitting ? 'Submitting…' : isSuperAdmin ? 'Apply Edit' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default RequestEditDialog;
